import { useEffect, useState } from 'react';
import {
  fetchShipFromConfig,
  saveShipFromConfig,
  type ShipFromConfig as ShipFrom,
} from '../../lib/saleroom-admin-queries';

/**
 * Admin → Saleroom → "Ship-from address" card.
 *
 * The dispatch address Shippo rates and prints labels FROM (Kate's). It feeds
 * the live postage estimate members see while bidding and the exact rate at
 * charge time. Stored in shipping_config (077); overrides the SHIP_FROM_* env
 * per-field, so leaving a box blank falls back to the env value.
 */

const EMPTY: ShipFrom = {
  ship_from_name: '', ship_from_street1: '', ship_from_street2: '',
  ship_from_city: '', ship_from_state: '', ship_from_zip: '',
  ship_from_country: 'US', ship_from_phone: '', ship_from_email: '',
};

function fromApi(c: ShipFrom | null): ShipFrom {
  if (!c) return { ...EMPTY };
  return {
    ship_from_name: c.ship_from_name ?? '',
    ship_from_street1: c.ship_from_street1 ?? '',
    ship_from_street2: c.ship_from_street2 ?? '',
    ship_from_city: c.ship_from_city ?? '',
    ship_from_state: c.ship_from_state ?? '',
    ship_from_zip: c.ship_from_zip ?? '',
    ship_from_country: c.ship_from_country ?? 'US',
    ship_from_phone: c.ship_from_phone ?? '',
    ship_from_email: c.ship_from_email ?? '',
  };
}

export function ShipFromConfig() {
  const [form, setForm] = useState<ShipFrom>(EMPTY);
  const [tokenConfigured, setTokenConfigured] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchShipFromConfig()
      .then((res) => {
        if (cancelled) return;
        setForm(fromApi(res.config));
        setTokenConfigured(res.token_configured);
      })
      .catch((err) => { if (!cancelled) setError(err?.message ?? 'Could not load the ship-from address.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  function set<K extends keyof ShipFrom>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  }

  // Shippo needs these to rate/label; state is required for US.
  const valid =
    form.ship_from_name!.trim() !== '' && form.ship_from_street1!.trim() !== '' &&
    form.ship_from_city!.trim() !== '' && form.ship_from_zip!.trim() !== '' &&
    (form.ship_from_country ?? '').trim().length === 2 &&
    ((form.ship_from_country ?? '').toUpperCase() !== 'US' || form.ship_from_state!.trim() !== '');

  async function save() {
    if (busy || !valid) return;
    setBusy(true);
    setError(null);
    try {
      const res = await saveShipFromConfig(form);
      setForm(fromApi(res.config));
      setTokenConfigured(res.token_configured);
      setSaved(true);
    } catch (err: any) {
      setError(err?.message ?? 'Could not save the ship-from address.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="shipfrom">
      <div className="admin__panel-head shipfrom__head">
        <h2>Ship-from address</h2>
      </div>
      <p className="shipfrom__lede">
        The dispatch address Shippo prices postage and prints labels from. It powers the live
        postage estimate members see while bidding and the exact rate charged to the winner.
        Blank fields fall back to the server's <code>SHIP_FROM_*</code> settings.
      </p>

      {tokenConfigured === false && (
        <p className="shipfrom__warn" role="status">
          No Shippo API token is configured yet, so postage stays on the flat per-lot figure.
          Add <code>SHIPPO_API_TOKEN</code> to enable live rates.
        </p>
      )}

      {loading ? (
        <p className="shipfrom__loading">Loading…</p>
      ) : (
        <div className="shipfrom__form">
          <label className="shipfrom__field shipfrom__field--wide">
            <span>Name / business</span>
            <input value={form.ship_from_name ?? ''} onChange={(e) => set('ship_from_name', e.target.value)}
                   disabled={busy} placeholder="The Converted Closet" />
          </label>
          <label className="shipfrom__field shipfrom__field--wide">
            <span>Street address</span>
            <input value={form.ship_from_street1 ?? ''} onChange={(e) => set('ship_from_street1', e.target.value)}
                   disabled={busy} placeholder="123 Main St" />
          </label>
          <label className="shipfrom__field shipfrom__field--wide">
            <span>Apt / unit / suite <em>(optional)</em></span>
            <input value={form.ship_from_street2 ?? ''} onChange={(e) => set('ship_from_street2', e.target.value)}
                   disabled={busy} />
          </label>
          <label className="shipfrom__field">
            <span>City</span>
            <input value={form.ship_from_city ?? ''} onChange={(e) => set('ship_from_city', e.target.value)}
                   disabled={busy} />
          </label>
          <label className="shipfrom__field">
            <span>State <em>(2-letter, e.g. CA)</em></span>
            <input value={form.ship_from_state ?? ''} onChange={(e) => set('ship_from_state', e.target.value)}
                   disabled={busy} maxLength={2} placeholder="CA" />
          </label>
          <label className="shipfrom__field">
            <span>ZIP / postcode</span>
            <input value={form.ship_from_zip ?? ''} onChange={(e) => set('ship_from_zip', e.target.value)}
                   disabled={busy} />
          </label>
          <label className="shipfrom__field">
            <span>Country <em>(2-letter)</em></span>
            <input value={form.ship_from_country ?? ''} onChange={(e) => set('ship_from_country', e.target.value.toUpperCase())}
                   disabled={busy} maxLength={2} placeholder="US" />
          </label>
          <label className="shipfrom__field">
            <span>Phone <em>(optional)</em></span>
            <input value={form.ship_from_phone ?? ''} onChange={(e) => set('ship_from_phone', e.target.value)}
                   disabled={busy} />
          </label>
          <label className="shipfrom__field">
            <span>Email <em>(optional)</em></span>
            <input value={form.ship_from_email ?? ''} onChange={(e) => set('ship_from_email', e.target.value)}
                   disabled={busy} type="email" />
          </label>
        </div>
      )}

      {error && <p className="shipfrom__error" role="alert">{error}</p>}

      {!loading && (
        <div className="shipfrom__actions">
          <button type="button" className="btn btn--primary btn--md" onClick={save} disabled={busy || !valid}>
            {busy ? 'Saving…' : 'Save ship-from address'}
          </button>
          {saved && <span className="shipfrom__saved" role="status">Saved.</span>}
          {!valid && !busy && (
            <span className="shipfrom__hint">Name, street, city, ZIP, country (and state for US) are required.</span>
          )}
        </div>
      )}
    </div>
  );
}
